import { deferredCallback } from "@/utils/Debouncer";
import { defineStore } from "pinia";
import {
	connectImageStream,
	disconnectImageStream
} from "vtk.js/Sources/Rendering/Misc/RemoteView";
import { useApiStore as api } from "../apiStore";
import { useConfigStore as config } from "../configStore";
import { useHierarchyStore } from "../hierarchyStore";
import { useActiveStore as active } from "./active";
import { useLayerStore as layers } from "./layer";
import { usePresetsStore as presets } from "./presets";
import { usePvwStore as pvw } from "./pvw";
import { useViewStore as views } from "./views";
import { useWslinkStore as wslink } from "./wslink";
// import _ from "lodash";
// ----------------------------------------------------------------------------
let BATCH_LISTENERS = {} as any;
let NETWORK_GUARD = {} as any;
// const NETWORK_DEBOUNCE = {} as any;
// ----------------------------------------------------------------------------
function extractViewIds(layout: any, ids: any = []) {
	for (let i = 0; i < layout.length; i++) {
		const item: any = layout[i];
		if (item.viewId) {
			ids.push(item.viewId);
		}
		if (item.content) {
			extractViewIds(item.content, ids);
		}
	}
	return ids;
}

// ----------------------------------------------------------------------------

function netGuard(keys: any, values: any) {
	const key = keys.join("::");
	const value = values.join("::");
	const changeDetected = NETWORK_GUARD[key] !== value;
	NETWORK_GUARD[key] = value;
	return changeDetected;
}

// ----------------------------------------------------------------------------

function clearNetworkGuard(keyStartName?: any) {
	Object.keys(NETWORK_GUARD).forEach((key) => {
		if (keyStartName) {
			if (key.indexOf(keyStartName) === 0) {
				delete NETWORK_GUARD[key];
			}
		} else {
			delete NETWORK_GUARD[key];
		}
	});
}

// ----------------------------------------------------------------------------

// const createDebounce = (dispatch:any, action:any) => {
//   let busy = false;
//   let pending = false;
//   let lastArg = null as any;
//   let req = null as any;

//   const run = (value:any) => {
//     if (busy) {
//       pending = true;
//       lastArg = value;
//       return req;
//     }
//     busy = true;
//     req = dispatch(action, value)
//       .then(() => {
//         busy = false;
//         if (pending) {
//           pending = false;
//           run(lastArg);
//         }
//       })
//       .catch(() => {
//         busy = false;
//         if (pending) {
//           pending = false;
//           run(lastArg);
//         }
//       });
//     return req;
//   };
//   return run;
// }

// ----------------------------------------------------------------------------

// const netDebounce = (key:any, dispatch:any, action:any, arg:any) => {
//   if (!NETWORK_DEBOUNCE[key]) {
//     NETWORK_DEBOUNCE[key] = createDebounce(dispatch, action);
//   }
//   NETWORK_DEBOUNCE[key](arg);
// }

// ----------------------------------------------------------------------------

export const useSsaStore = defineStore("ssaStore", {
	state: () =>
		({
			initialized: false
		}) as any,
	actions: {
		async SSA_DISCONNECT() {
			BATCH_LISTENERS = {};
			//   NETWORK_DEBOUNCE = {};
			NETWORK_GUARD = {};
			await active().SSA_ACTIVE_RESET();
			await layers().SSA_LAYER_RESET();
			// await dispatch('SSA_PICK_RESET');
			//  await this.SSA_RESET();
			await disconnectImageStream();
			await wslink().WSLINK_DISCONNECT();
		},
		async SSA_INITIALIZE() {
			if (this.initialized) {
				return;
			}
			this.initialized = true;
			try {
				// --------------------------------------------------
				// Initialize image stream
				// --------------------------------------------------

				connectImageStream(wslink().client.getConnection().getSession());

				// --------------------------------------------------
				// Configure mine to use
				// --------------------------------------------------
				await pvw().PVW_LOAD_MINE(`${api().config?.domain}/_data/${api().config?.domain}`);
				// Configure Paraview config for hierarchy

				useHierarchyStore().SSA_CONFIGURE_PARAVIEW();
				// --------------------------------------------------
				// Start registering listeners
				// --------------------------------------------------

				// - views
				BATCH_LISTENERS.views = deferredCallback((l: any) =>
					views().SSA_VIEW_STATE_UPDATE(l)
				);
				pvw().PVW_SUBSCRIBE_VIEW_STATE(BATCH_LISTENERS.views.listener);
				// - layers
				BATCH_LISTENERS.layers = deferredCallback((l: any) =>
					layers().SSA_LAYER_STATE_UPDATE(l)
				);
				pvw().PVW_SUBSCRIBE_LAYER_STATE(BATCH_LISTENERS.layers.listener);
				// - representations
				BATCH_LISTENERS.representations = deferredCallback((l: any) =>
					layers().SSA_REPRESENTATION_STATE_UPDATE(l)
				);
				pvw().PVW_SUBSCRIBE_REPRESENTATION_STATE(BATCH_LISTENERS.representations.listener);

				// - selections
				// ========= TODO PVW SELECTION SIZE =============
				// BATCH_LISTENERS.selections = deferredCallback((l:any) =>
				//   dispatch('SSA_SELECTION_SIZE_UPDATE', l)
				// );
				// dispatch('PVW_SUBSCRIBE_SELECTION_SIZE', BATCH_LISTENERS.selections.listener);
				// - more... ???
				// ========= END TODO PVW SELECTION SIZE =============

				// --------------------------------------------------
				// Ask for full server state and flush any batch listener
				// --------------------------------------------------
				await pvw().PVW_PUSH_SERVER_STATE();
				Object.values(BATCH_LISTENERS).forEach(({ flush }: any) => flush());

				// --------------------------------------------------
				// Handle view layout
				// --------------------------------------------------

				const layout = await pvw().PVW_LAYOUT_GET();
				//commit('SSA_LAYOUT_SET', layout);
				const viewIds = extractViewIds(layout);

				const defaultViewId = (config() as any).config.paraview_defaults.defaultViewId;

				for (let i = 0; i < viewIds.length; i++) {
					if (viewIds[i] != defaultViewId) {
						await pvw().PVW_REMOVE_VIEW({ viewId: viewIds[i] });
					}
				}
				views().SSA_VIEW_IDS_SET([defaultViewId]);
				active().SSA_ACTIVE_VIEW_SET(defaultViewId);

				// --------------------------------------------------
				// Fetch image presets from server
				// --------------------------------------------------

				const listToFetch = presets().SSA_PRESETS_ITEMS.map(
					({ value }: { value: any }) => value
				);
				const fetch = () => {
					if (!listToFetch.length) {
						return;
					}
					const presetName = listToFetch.pop();
					const promises = [
						presets().SSA_PRESETS_FETCH({ presetName, invert: false }),
						presets().SSA_PRESETS_FETCH({ presetName, invert: true })
					];
					Promise.all(promises).then(fetch);
				};
				await fetch();
			} catch (e) {
				console.log(e);
			}
		},
		async SSA_RESET_SESSION() {
			const viewToDelete: any = [];
			const viewToReset = views().SSA_VIEW_IDS;
			//commit('SSA_APPLICATION_STATE_LOADING_SET', true);  // SET APP LOADING TRUE
			await layers().SSA_LAYER_RESET;
			await active().SSA_ACTIVELAYER_RESET;
			await clearNetworkGuard("COLOR_BY");
			// commit('LOADED_PIECES_RESET'); // SET LOADED PIECES TO NOTHING
			for (let i = 0; i < viewToReset.length; i++) {
				layers().SSA_LAYER_RESET_VIEW(viewToReset[viewToReset[i]]);
			}
			await pvw().PVW_APPLICATION_STATE_RESET_VIEWS({ viewToReset, viewToDelete });
			// commit('SSA_APPLICATION_STATE_LOADING_SET', false); // SET APP LOADING FALSE
		},

		async SSA_RESET() {
			await pvw().PVW_UNSUBSCRIBE_VIEW_STATE();
			await pvw().PVW_UNSUBSCRIBE_LAYER_STATE();
			await pvw().PVW_UNSUBSCRIBE_REPRESENTATION_STATE();
			// await pvw().PVW_UNSUBSCRIBE_SELECTION_SIZE();

			// Clear network guard
			await clearNetworkGuard();

			// Reset initialization
			this.initialized = false;
		},
		// ------------------------------------------------------------------------
		async SSA_UPDATE_ACTIVE_VIEW_STATE(changeSet: any) {
			const viewId = active().SSA_ACTIVE_VIEW;
			//const key = `${viewId}-${Object.keys(changeSet).join('_')}`;
			return pvw().PVW_VIEW_STATE_UPDATE({ viewId, changeSet });
			// return netDebounce(key, dispatch, 'PVW_VIEW_STATE_UPDATE', {
			//   viewId,
			//   changeSet,
			// });
		},
		async SSA_TOGGLE_ACTIVE_AXES_GRID() {
			const viewState = views().SSA_VIEW_ACTIVE_STATE;

			const gridVisibility = viewState && viewState.gridVisibility ? 0 : 1;
			return this.SSA_UPDATE_ACTIVE_VIEW_STATE({ gridVisibility });
		},
		async SSA_TOGGLE_ACTIVE_VIEW_BACKGROUND() {
			const viewState = views().SSA_VIEW_ACTIVE_STATE;
			if (viewState) {
				const viewId = viewState.viewId;
				const changeSet = {
					background: viewState.foreground,
					foreground: viewState.background
				};
				return pvw().PVW_VIEW_STATE_UPDATE({ viewId, changeSet });
			}
			return Promise.reject("No active view to toggle background");
		},
		async SSA_TOGGLE_ACTIVE_VIEW_PROJECTION_MODE() {
			const viewState = views().SSA_VIEW_ACTIVE_STATE;
			if (viewState) {
				const viewId = viewState.viewId;
				const changeSet = {
					cameraParallelProjection: !viewState.cameraParallelProjection
				};
				return pvw().PVW_VIEW_STATE_UPDATE({ viewId, changeSet });
			}
			return Promise.reject("No active view to toggle projection mode");
		},
		async SSA_REPRESENTATION_UPDATE_PROPERTY({
			layerName,
			viewId,
			type,
			name,
			value
		}: {
			layerName: string;
			viewId: string;
			type: string;
			name: string;
			value: any;
		}) {
			// const key = `${layerName}-${viewId}-${type}-${name}`;
			pvw().PVW_REPRESENTATION_UPDATE_PROPERTY({ layerName, viewId, type, name, value });
			// return netDebounce(key, dispatch, 'PVW_REPRESENTATION_UPDATE_PROPERTY', {

			// });
		},
		async SSA_COLOR_BY_UPDATE({
			layerName,
			viewId,
			field,
			fieldList,
			colorRange,
			colorType,
			fieldLabel,
			presetName,
			preset,
			presetInvert,
			annotations,
			rgbPoints,
			indexedColors,
			indexedOpacities,
			forceCategoricalComputation,
			useLogScale
		}: {
			layerName: string;
			viewId: string;
			field: string;
			fieldList: string[];
			colorRange: any;
			colorType: string;
			fieldLabel: string;
			presetName: string;
			preset: any;
			presetInvert: any;
			annotations: any;
			rgbPoints: any;
			indexedColors: any;
			indexedOpacities: any;
			forceCategoricalComputation: any;
			useLogScale: any;
		}) {
			// Prevent multiple request with same setup
			const check = netGuard(
				["COLOR_BY", layerName, viewId],
				[
					field,
					fieldList,
					colorRange,
					colorType,
					fieldLabel,
					presetName,
					preset,
					presetInvert,
					annotations,
					rgbPoints,
					indexedColors,
					indexedOpacities,
					forceCategoricalComputation,
					useLogScale
				]
			);
			if (check) {
				if (field == null || field == "") return;
				const post: any = {
					layerName,
					viewId,
					field,
					fieldList,
					colorRange,
					colorType,
					fieldLabel,
					presetName,
					preset,
					presetInvert,
					annotations,
					rgbPoints,
					indexedColors,
					indexedOpacities,
					forceCategoricalComputation,
					useLogScale
				};
				if (layerName.toLowerCase().indexOf("survey") > -1) delete post.fieldList;
				// console.log("    🚀🚀 SENDING colorBy update: ", annotations);
				await pvw().PVW_COLOR_BY_UPDATE(post);
			}

			return Promise.resolve();
		},
		async SSA_CATEGORY_OPACITY_UPDATE({
			layerName,
			viewId,
			field,
			indexedOpacities
		}: {
			layerName: string;
			viewId: string;
			field: string;
			indexedOpacities: any;
		}) {
			//const key = `${viewId}-${layerName}-${field}`;
			// return netDebounce(key, dispatch, 'PVW_COLOR_BY_UPDATE', {
			//   layerName,
			//   viewId,
			//   field,
			//   indexedOpacities,
			// });
			return pvw().PVW_COLOR_BY_UPDATE({
				layerName,
				viewId,
				field,
				indexedOpacities
			});
		},
		async SSA_HISTOGRAM_FETCH({
			layerName,
			viewId,
			field,
			dataRange,
			numberOfSamples
		}: {
			layerName: string;
			viewId: string;
			field: string;
			dataRange: any;
			numberOfSamples: any;
		}) {
			return pvw().PVW_HISTOGRAM_FETCH({
				layerName,
				viewId,
				field,
				dataRange,
				numberOfSamples
			});
		},
		// ------------------------------------------------------------------------
		async SSA_UPDATE_ACTIVE_SCALARBAR() {
			const viewId = active().SSA_ACTIVE_VIEW;
			const layerName = active().SSA_ACTIVE_LAYER;
			if (netGuard(["ACTIVE_SCALARBAR", viewId], [layerName])) {
				return pvw().PVW_VIEW_LAYER_ACTIVATE({ layerName, viewId });
			}
		},
		async SSA_UPDATE_ALL_SCALARBARS(layerName: string) {
			const allViews = views().SSA_VIEW_IDS;
			for (let i = 0; i < allViews.length; i++) {
				pvw().PVW_VIEW_LAYER_ACTIVATE({ layerName, viewId: allViews[i] });
			}
			return Promise.resolve();
		}
	}
});
